import {Map as IMap, OrderedMap as IOrderedMap} from 'immutable';
import {last, curry} from 'lodash';
import {
  increment,
  incrementSlug,
  slugify,
} from '@packages/utilities/string-utils';
import {replace} from '@packages/utilities/fp/utils';
import {getElse} from '@packages/utilities/data-utils';
import {get} from '@packages/utilities/imm4-backports';
export {slugify} from '@packages/utilities/string-utils';

/**
 * @import {RecordOf} from "immutable"
 */

/**
 * Any value that can provide an ID via:
 * - Direct `_id` or `id` property access (plain objects)
 * - `.get('_id')` or `.get('id')` method (Immutable Records/Maps)
 * @template {string|ObjectId} [Id=string|ObjectId]
 * @typedef {(
 *   | { id?: Id, _id?: Id }
 *   | { get(key: "id" | "_id"): Id }
 * )} Idable
 */

/**
 * Curried. Returns `true` when both arguments resolve to the same id via `getId`.
 *
 * @type {{
 *   (a: Idable | null | undefined): (b: Idable | null | undefined) => boolean;
 *   (a: Idable | null | undefined, b: Idable | null | undefined): boolean;
 * }}
 */
export const isSameIdentity = curry(function isSameIdentity(a, b) {
  return a && b && getId(a) === getId(b);
});

/**
 * Compares two Immutable.js records/maps for value equality (ignoring itemCount).
 * @param {RecordOf<any> | IMap<string, any>} first
 * @param {RecordOf<any> | IMap<string, any>} second
 * @returns {boolean}
 */
export const isSameValue = (first, second) => {
  const firstRemoved = first.remove('itemCount');
  const secondRemoved = second.remove('itemCount');

  return firstRemoved.equals(secondRemoved);
};

/**
 * @param {RecordOf<any> | IMap<string, any> | null | undefined} a
 * @param {RecordOf<any> | IMap<string, any> | null | undefined} b
 * @returns {boolean}
 */
export const isModified = (a, b) => a && b && !isSameValue(a, b);

/**
 * @param {Idable} record
 * @returns {string}
 */
export function getId(record) {
  return (
    record._id ||
    record.id ||
    /* ⚡️PERF WARNING ⚠️:
      do not put record.get('id') as #get second parameter here because
      it will always be evaluated, often unnecessarily
    */
    (record.get ? record.get('_id') || record.get('id') : null)
  );
}

export function isMintId(id) {
  return typeof id === 'string' && id.startsWith('mint-');
}

export function isMint(record) {
  // Predicate is invoked inside immutable.js `updateIn`/Ramda `when` chains
  // where the value at the keyPath can transiently be null or a non-Map
  // (see issue #29936). Treat unknown shapes as "not mint" rather than throw.
  if (!record || typeof record.get !== 'function') return false;
  return record.get('isMint', isMintId(String(getId(record))));
}

/**
 * Returns if the record was created from duplication flow
 * @param {Record} record
 * @returns {Boolean} If record was created from duplication flow
 */
export function pageWasCreatedFromDuplicationFlow(record) {
  const duplicateOfIsSet = record.get('duplicateOf') !== '';

  return duplicateOfIsSet && record.get('pageWasCreatedFromDuplicationFlow');
}

/**
 * Returns if record is persisted (is not mint and has a valid id)
 * @param  {Record}  record
 * @return {Boolean}        If record is persisted on the server
 */
export function isPersisted(record) {
  return !isMint(record) && !String(getId(record)).startsWith('not-persisted-');
}

export function makeMintId() {
  return 'mint-' + makeId();
}

export function makeNonPersistedId() {
  return 'not-persisted-' + makeId();
}

export function makeId() {
  return Date.now() + '-' + Math.random().toString().slice(2);
}

// A version of slugify that will convert trailing spaces to a trailing -; useful for cases where the user is typing a slug live
export function slugifyLive(str) {
  //if the slug is empty the user can't type '-' or '<space>'
  const slug = str !== '-' ? slugify(str) : '';
  //if the slug is NOT empty && it DOESN'T have a '-' at the end && have a space at the end then it adds the '-'
  return slug !== '' && !/-$/.test(slug) && /[ \xA0]$/.test(str)
    ? slug + '-'
    : slug;
}

export function getSlug(record) {
  const slug = record.get('slug');
  return slug || slugify(record.get('name'));
}

export const syncSlug = curry((key, records, record) => {
  return record.set('slug', uniqueSlug(slugify(record.get(key)), records));
});

export const syncDeepSlug = curry((slugPath, keyPath, records, record) =>
  record.setIn(slugPath, slugify(record.getIn(keyPath)), records.toJS())
);

export function getSlugValue(record, opts = {}) {
  const target = opts.secondaryLocaleId
    ? record.getIn(['localizedSettings', opts.secondaryLocaleId], IMap())
    : record;
  return last(getElse('slug', '', target).split('/'));
}

/**
 * stripCopyText :: String -> String
 **
 * Strips the provided string of characters suspected to be artifacts of a
 * previous copy.
 */
const stripCopyText = replace(/(([ |-][C|c]opy)([ |-]\d{1,2})?)+$/)('');

// * :: String -> [Record] -> String
export const uniqueName = (prefix, items) =>
  increment(items.map(getElse('name', '')), prefix, ' ');

export const uniqueSlug = (prefix, items, opts) => {
  const localeId = opts?.localeId;

  const alreadyUsedSlugs = localeId
    ? items
        .map(getElse('localizedSettings', {}))
        .map(getElse(localeId, {}))
        .map(getElse('slug', ''))
    : items.map(getElse('slug', ''));

  return incrementSlug(alreadyUsedSlugs, prefix, '-');
};

export const uniqueNameCopy = (prefix, items) =>
  uniqueName(stripCopyText(prefix) + ' Copy', items);

export const uniqueSlugCopy = (prefix, items, opts) => {
  const localeId = opts?.localeId;
  return uniqueSlug(stripCopyText(prefix) + '-copy', items, {localeId});
};

/**
 * @template {Idable} T
 * @template [U=T]
 * @param {Iterable<T>} coll
 * @param {(item: T, id: string) => U} [mapper]
 * @returns {IMap<string, U>}
 */
export const indexById = (coll, mapper) => {
  const imap = IMap().asMutable();

  for (const item of coll) {
    const id = getId(item);
    imap.set(id, typeof mapper === 'function' ? mapper(item, id) : item);
  }

  return imap.asImmutable();
};

/**
 * @template {Idable} T
 * @template [U=T]
 * @param {Iterable<T>} coll
 * @param {(item: T, id: string) => U} [mapper]
 * @returns {IOrderedMap<string, U>}
 */
export const orderedIndexById = (coll, mapper) => {
  const iOrderedMap = IOrderedMap().asMutable();

  for (const item of coll) {
    const id = getId(item);
    iOrderedMap.set(id, typeof mapper === 'function' ? mapper(item, id) : item);
  }

  return iOrderedMap.asImmutable();
};

export const isDraft = (record) => get(record, '_draft', false);

export const isArchived = (record) => get(record, '_archived', false);
